import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';
import Heading from '@atlaskit/heading/heading';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import Image from '@atlaskit/image/image';
import Link from '@atlaskit/link/link';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import { openLoomMeetingRecordings } from '@atlassian/loom-meeting-recordings';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import type { PlacementFlags } from '@post-office/placements--growth/confluence-side-nav';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { requestLoomMeetingNudgeReminder } from '../../../reminder-event';
import { useLoomMeetingNudgeAnalytics } from '../../../analytics';
import AiNotesIcon from './assets/ai-notes.svg';
import { messages } from './messages';

type MeetingNudgeBannerProps = {
	cloudId: string;
	handleOnDismissed: () => void;
	flags: PlacementFlags;
};

const DATA_PRIVACY_URL = 'https://www.atlassian.com/legal/privacy-policy';
const NOT_INTERESTED_FLAG_ID = 'loom-meeting-nudge-not-interested';

const getPostOfficeDismissEvent = (
	clickIdentifier: 'connect-calendar' | 'not-interested' | 'remind-me',
	buttonType: 'primary' | 'secondary' = 'secondary',
) => ({
	messageEventType: 'clicked' as const,
	clickedElement: {
		clickIdentifier,
		clickedElement: 'button' as const,
		buttonType,
	},
	action: { actionType: 'dismiss' as const },
	additionalMetadataFromFrontend: {},
});

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '100%',
	},
	action: {
		alignItems: 'center',
		backgroundColor: 'transparent',
		display: 'inline-flex',
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
		color: token('color.link'),
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	aiNotesIcon: {
		height: '40px',
		width: '40px',
	},
	privacyLinkContainer: {
		marginTop: token('space.150'),
	},
});

export const MeetingNudgeBanner = ({
	cloudId,
	handleOnDismissed,
	flags,
}: MeetingNudgeBannerProps): JSX.Element => {
	const intl = useIntl();
	const [isNotInterestedFlagOpen, setIsNotInterestedFlagOpen] = useState(false);
	const [isWhyModalOpen, setIsWhyModalOpen] = useState(false);
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { fireCtaClicked, fireScreenSeen, fireFlagDismissed } =
		useLoomMeetingNudgeAnalytics('sidenav');
	const reminderConfirmation = {
		title: intl.formatMessage(messages.reminderTitle),
		description: intl.formatMessage(messages.reminderDescription),
	};
	const notInterestedConfirmation = {
		title: intl.formatMessage(messages.notInterestedTitle),
		description: intl.formatMessage(messages.notInterestedDescription),
		undo: intl.formatMessage(messages.notInterestedUndo),
	};

	useEffect(() => {
		if (isWhyModalOpen) {
			fireScreenSeen('whyAmISeeingThis');
		}
	}, [fireScreenSeen, isWhyModalOpen]);

	const finishNudgeFlow = () => {
		setIsNotInterestedFlagOpen(false);
		handleOnDismissed();
	};

	const handleConnectCalendar = () => {
		fireCtaClicked('connectCalendar');
		void messageInstanceClicked(getPostOfficeDismissEvent('connect-calendar', 'primary'));
		openLoomMeetingRecordings({
			bridge: 'loom-meeting-nudge-sidenav',
			cloudId,
			interactionSource: 'confluence',
			loomSource: 'confluence-nudge-sidenav',
		});
		finishNudgeFlow();
	};

	const handleRemindMe = () => {
		fireCtaClicked('remindMe');
		void messageInstanceClicked(getPostOfficeDismissEvent('remind-me'));
		requestLoomMeetingNudgeReminder();
		void flags.showFlag({
			id: 'loom-meeting-nudge-reminder-confirmation',
			type: 'success-circle',
			title: reminderConfirmation.title,
			description: reminderConfirmation.description,
			customComponent: AutoDismissFlag,
			close: 'never',
			onShown: () => fireScreenSeen('remindMe'),
			onClose: () => fireFlagDismissed('flagRemindMe'),
		});
		finishNudgeFlow();
	};

	const handleNotInterestedConfirmationDismissed = () => {
		void messageInstanceClicked(getPostOfficeDismissEvent('not-interested'));
		fireFlagDismissed('flagNotInterested');
		finishNudgeFlow();
	};

	const handleNotInterestedUndo = () => {
		void flags.hideFlag(NOT_INTERESTED_FLAG_ID);
		fireCtaClicked('undo');
		finishNudgeFlow();
	};

	const handleNotInterested = () => {
		fireCtaClicked('notInterested');
		setIsNotInterestedFlagOpen(true);
		void flags.showFlag({
			id: NOT_INTERESTED_FLAG_ID,
			type: 'success-circle',
			title: notInterestedConfirmation.title,
			description: notInterestedConfirmation.description,
			actions: [
				{
					content: notInterestedConfirmation.undo,
					onClick: handleNotInterestedUndo,
				},
			],
			customComponent: AutoDismissFlag,
			close: 'never',
			onShown: () => fireScreenSeen('notInterested'),
			onClose: handleNotInterestedConfirmationDismissed,
		});
	};

	const handleWhyAmISeeingThis = () => {
		fireCtaClicked('whyAmISeeingThis');
		setIsWhyModalOpen(true);
	};

	const handleCloseWhyAmISeeingThis = () => {
		setIsWhyModalOpen(false);
	};

	return (
		<>
			{!isNotInterestedFlagOpen && (
				<Box xcss={styles.container}>
					<Stack space="space.100">
						<Inline alignBlock="start" spread="space-between">
							<Box xcss={styles.aiNotesIcon}>
								<Image
									src={AiNotesIcon}
									alt=""
									width="40"
									height="40"
									testId="loom-meeting-nudge-ai-notes-icon"
								/>
							</Box>
							<DropdownMenu
								trigger={({ triggerRef, onClick, ...triggerProps }) => (
									<IconButton
										{...triggerProps}
										appearance="subtle"
										icon={ShowMoreHorizontalIcon}
										label={intl.formatMessage({
											id: 'post-office.twc-loom-meeting-nudge-sidenav.more-actions',
											defaultMessage: 'More actions',
											description: 'Label for the Loom meeting nudge options menu',
										})}
										onClick={(event) => {
											fireCtaClicked('more');
											onClick?.(event);
										}}
										ref={triggerRef}
										spacing="compact"
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem onClick={handleNotInterested}>
										<FormattedMessage
											id="post-office.twc-loom-meeting-nudge-sidenav.not-interested"
											defaultMessage="Not interested"
											description="Action to permanently turn off Loom meeting recording suggestions"
										/>
									</DropdownItem>
									<DropdownItem onClick={handleWhyAmISeeingThis}>
										<FormattedMessage
											id="post-office.twc-loom-meeting-nudge-sidenav.why-am-i-seeing-this"
											defaultMessage="Why am I seeing this?"
											description="Action to explain why the Loom meeting nudge was shown"
										/>
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</Inline>

						<Heading color="color.text" size="xsmall">
							<FormattedMessage
								id="post-office.twc-loom-meeting-nudge-sidenav.heading"
								defaultMessage="Turn meetings into action"
								description="Heading for a side navigation banner promoting Loom meeting recordings"
							/>
						</Heading>
						<Text as="p" color="color.text.subtle">
							<FormattedMessage
								id="post-office.twc-loom-meeting-nudge-sidenav.body"
								defaultMessage="Loom captures action items in Confluence and Jira so you don’t have to."
								description="Body copy for a side navigation banner promoting Loom meeting recordings"
							/>
						</Text>
						<Inline alignBlock="center" space="space.100">
							<Pressable onClick={handleConnectCalendar} xcss={styles.action}>
								<Text as="p" color="color.link">
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.connect-calendar"
										defaultMessage="Connect calendar"
										description="Action to open Loom's calendar connection flow"
									/>
								</Text>
							</Pressable>
							<span aria-hidden="true">·</span>
							<Pressable onClick={handleRemindMe} xcss={styles.action}>
								<Text as="p" color="color.link">
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.remind-me"
										defaultMessage="Remind me"
										description="Action to defer the Loom calendar reminder until the next publish or live-page load"
									/>
								</Text>
							</Pressable>
						</Inline>
					</Stack>
				</Box>
			)}
			<ModalTransition>
				{isWhyModalOpen && (
					<ModalDialog onClose={handleCloseWhyAmISeeingThis} width="large">
						<ModalHeader>
							<ModalTitle>
								<FormattedMessage
									id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.title"
									defaultMessage="Why am I seeing this?"
									description="Title for an explanation of why the Loom meeting nudge was shown"
								/>
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Text as="p">
								<FormattedMessage
									id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.introduction.ai-non-final"
									defaultMessage="We think <strong>Loom</strong> will improve your team’s productivity. The recommendation is based on:"
									description="Introduction in the Loom meeting nudge explanation modal describing why Atlassian recommends Loom to the user"
									values={{
										strong: (chunks) => <Text weight="bold">{chunks}</Text>,
									}}
								/>
							</Text>
							<ul>
								<li>
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.teamwork-collection.ai-non-final"
										defaultMessage="your access to the Teamwork Collection (which includes Loom)"
										description="First reason in the Loom meeting nudge explanation modal, describing the user’s access to the Teamwork Collection"
									/>
								</li>
								<li>
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.atlassian-tools.ai-non-final"
										defaultMessage="the tools you use (Confluence and Jira)"
										description="Second reason in the Loom meeting nudge explanation modal, identifying the Atlassian tools the user already uses"
									/>
								</li>
								<li>
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.meeting-workflow.ai-non-final"
										defaultMessage="the way you manage meetings and documentation in your existing workflow"
										description="Third reason in the Loom meeting nudge explanation modal, describing how the recommendation relates to the user’s workflow"
									/>
								</li>
							</ul>
							<Box xcss={styles.privacyLinkContainer}>
								<Link href={DATA_PRIVACY_URL} target="_blank">
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.privacy-link.ai-non-final"
										defaultMessage="Learn more about your data privacy with Atlassian"
										description="Link in the Loom meeting nudge explanation modal that opens Atlassian’s privacy policy in a new browser tab"
									/>
								</Link>
							</Box>
						</ModalBody>
						<ModalFooter>
							<Button appearance="primary" onClick={handleCloseWhyAmISeeingThis}>
								<FormattedMessage
									id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.close.ai-non-final"
									defaultMessage="OK"
									description="Confirmation button that closes the Loom meeting nudge explanation modal"
								/>
							</Button>
						</ModalFooter>
					</ModalDialog>
				)}
			</ModalTransition>
		</>
	);
};
